<template>
  <div class="content final-testing-attempt-page">
    <div class="content-header">
      <button class="content-header__exit" @click="logout">Выйти</button>
      <RouterLink :to="{ name: 'final-testing' }" class="content-header__close"><i class="fas fa-times"></i></RouterLink>
    </div>

    <div class="content-main">
      <div class="content-main_desc">
        <div v-if="isLoading" class="final-testing-state">Загрузка итогового тестирования...</div>

        <template v-else>
          <div class="attempt-hero">
            <div>
              <div class="attempt-hero__eyebrow">Итоговое тестирование</div>
              <h1 class="attempt-hero__title">Экзаменационная попытка №{{ attempt?.id }}</h1>
              <p class="attempt-hero__subtitle">Вопросы собраны из выбранных блоков случайным образом. После неудачной попытки повторное итоговое тестирование будет доступно через 3 дня. После успешной сдачи можно сразу начать новую попытку.</p>
              <div class="attempt-hero__chips">
                <span v-for="block in attemptBlocks" :key="block.id" class="attempt-hero__chip">{{ block.name }}</span>
              </div>
            </div>
            <div class="attempt-hero__stats">
              <div><span>Вопросов</span><strong>{{ questions.length }}</strong></div>
              <div><span>Ошибок допустимо</span><strong>{{ attempt?.allowed_wrong ?? 0 }}</strong></div>
              <div><span>Осталось</span><strong>{{ remainingLabel }}</strong></div>
            </div>
          </div>

          <div v-if="result" class="attempt-result" :class="result.success ? 'attempt-result--success' : 'attempt-result--warning'">
            <div>
              <div class="attempt-result__eyebrow">Результат попытки</div>
              <div class="attempt-result__score">{{ result.score }}%</div>
              <div class="attempt-result__summary">
                {{ result.success ? 'Итоговое тестирование пройдено успешно.' : 'Попытка завершена. Ниже можно посмотреть ошибки и пояснения.' }}
              </div>
              <div v-if="!result.success && result.blocked_until" class="attempt-result__blocked">Новая попытка будет доступна после {{ result.blocked_until }}</div>
            </div>
            <div class="attempt-result__stats">
              <div><span>Всего</span><strong>{{ result.counts?.all ?? 0 }}</strong></div>
              <div><span>Верно</span><strong>{{ result.counts?.right ?? 0 }}</strong></div>
              <div><span>Частично</span><strong>{{ result.counts?.part ?? 0 }}</strong></div>
              <div><span>Неверно</span><strong>{{ result.counts?.wrong ?? 0 }}</strong></div>
            </div>
          </div>

          <div v-if="questions.length" class="attempt-layout">
            <aside class="attempt-sidebar">
              <RouterLink :to="{ name: 'final-testing' }" class="attempt-sidebar__back">← К выбору блоков</RouterLink>
              <div class="attempt-sidebar__panel">
                <div class="attempt-sidebar__label">Экзамен</div>
                <div class="attempt-sidebar__time">{{ remainingLabel }}</div>
                <div class="attempt-sidebar__meta">{{ answeredCount }} из {{ questions.length }} заполнено</div>
                <div class="attempt-sidebar__progress"><span :style="{ width: `${progressPercent}%` }"></span></div>
                <div class="attempt-sidebar__rule">Можно допустить ошибок: <strong>{{ attempt?.allowed_wrong ?? 0 }}</strong></div>
                <div class="attempt-sidebar__grid">
                  <button
                    v-for="(question, index) in questions"
                    :key="question.id"
                    type="button"
                    class="attempt-sidebar__dot"
                    :class="dotClass(question)"
                    @click="setActiveQuestion(question.id)"
                  >{{ index + 1 }}</button>
                </div>
                <div class="attempt-sidebar__hint">{{ locked ? 'После проверки доступны подсказки к неверным ответам.' : 'Ответьте на каждый вопрос до истечения времени.' }}</div>
              </div>
              <button
                v-if="!locked"
                type="submit"
                form="final-testing-form"
                class="button primary wide attempt-sidebar__submit"
                :disabled="isSubmitting"
              >{{ isSubmitting ? 'Отправляем...' : 'Завершить итоговое тестирование' }}</button>
              <RouterLink v-else :to="{ name: 'final-testing' }" class="button secondary wide attempt-sidebar__submit">Вернуться к выбору блоков</RouterLink>
            </aside>

            <form id="final-testing-form" class="attempt-form" @submit.prevent="submitAttempt">
              <div v-if="generalError" class="attempt-form__error">{{ generalError }}</div>
              <section
                v-if="currentQuestion"
                :id="`question-${currentQuestion.id}`"
                class="attempt-card"
                :class="cardClass(currentQuestion)"
              >
                <div class="attempt-card__head">
                  <div>
                    <div class="attempt-card__index">Вопрос {{ currentQuestionNumber }}</div>
                    <h2 class="attempt-card__title">{{ currentQuestion.name }}</h2>
                    <div class="attempt-card__meta">
                      <span class="attempt-card__meta-chip">{{ currentQuestion.general_testing_name }}</span>
                      <span v-if="currentQuestion.test_name" class="attempt-card__meta-chip attempt-card__meta-chip--subtle">{{ currentQuestion.test_name }}</span>
                    </div>
                  </div>
                  <button v-if="canShowExplanation(currentQuestion)" type="button" class="attempt-card__help" @click="openExplanation(currentQuestion)">?</button>
                </div>

                <div class="attempt-card__body">
                  <div>
                    <p v-if="currentQuestion.description" class="attempt-card__description">{{ currentQuestion.description }}</p>

                    <div class="attempt-card__answers">
                      <template v-if="currentQuestion.type === 'one' || currentQuestion.type === 'multi'">
                        <label v-for="answer in currentQuestion.answers" :key="answer.id" class="attempt-choice" :class="choiceClass(currentQuestion, answer.id)">
                          <input v-if="currentQuestion.type === 'one'" :name="`question-${currentQuestion.id}`" type="radio" :value="answer.id" :checked="isSelected(currentQuestion, answer.id)" :disabled="isQuestionLocked(currentQuestion)" @change="submitSingleChoice(currentQuestion, answer.id)">
                          <input v-else type="checkbox" :value="answer.id" :checked="isSelected(currentQuestion, answer.id)" :disabled="isQuestionLocked(currentQuestion) || isMultiChoiceDisabled(currentQuestion, answer.id)" @change="toggleMultiAnswer(currentQuestion, answer.id, $event.target.checked)">
                          <span class="attempt-choice__control"></span>
                          <span class="attempt-choice__text">{{ answer.answer }}</span>
                          <span v-if="locked && isCorrectAnswer(currentQuestion, answer.id)" class="attempt-choice__mark attempt-choice__mark--correct">Верно</span>
                          <span v-else-if="locked && isWrongSelectedAnswer(currentQuestion, answer.id)" class="attempt-choice__mark attempt-choice__mark--wrong">Ваш ответ</span>
                        </label>
                      </template>

                      <template v-else-if="currentQuestion.type === 'compare'">
                        <div class="attempt-compare-grid">
                          <div v-for="answer in currentQuestion.answers" :key="answer.id" class="attempt-compare-row" :class="compareClass(currentQuestion, answer)">
                            <label class="attempt-compare-row__label" :for="`q${currentQuestion.id}_${answer.id}`">{{ answer.answer }}</label>
                            <div class="attempt-compare-row__field">
                              <select class="attempt-compare-row__select" :id="`q${currentQuestion.id}_${answer.id}`" :value="getCompareValue(currentQuestion, answer.id)" :disabled="isQuestionLocked(currentQuestion)" @change="updateCompareAnswer(currentQuestion, answer.id, $event.target.value)">
                                <option :value="undefined" disabled>Выберите вариант</option>
                                <option v-for="other in currentQuestion.compare_answers" :key="other" :value="other">{{ other }}</option>
                              </select>
                              <span class="attempt-compare-row__chevron"><i class="fas fa-chevron-down"></i></span>
                            </div>
                            <div v-if="locked && answer.answer_compare" class="attempt-compare-row__right-answer">Правильно: {{ answer.answer_compare }}</div>
                          </div>
                        </div>
                      </template>

                      <div v-else-if="currentQuestion.type === 'text'" class="attempt-text-answer" :class="textClass(currentQuestion)">
                        <label class="attempt-text-answer__label" :for="`q${currentQuestion.id}`">Ваш ответ</label>
                        <textarea class="attempt-text-answer__field" :id="`q${currentQuestion.id}`" :value="getTextAnswer(currentQuestion)" @input="updateTextAnswer(currentQuestion, $event.target.value)" :disabled="isQuestionLocked(currentQuestion)" placeholder="Введите ответ в свободной форме" @blur="finalizeTextAnswer(currentQuestion)"></textarea>
                      </div>

                      <div v-if="questionError(currentQuestionIndex)" class="attempt-question-error">{{ questionError(currentQuestionIndex) }}</div>
                    </div>
                  </div>

                  <div v-if="currentQuestion.image_url" class="attempt-card__media"><img :src="currentQuestion.image_url" alt="Иллюстрация к вопросу"></div>
                </div>

                <div class="attempt-card__nav">
                  <button type="button" class="button secondary attempt-card__nav-btn" :disabled="currentQuestionIndex <= 0" @click="goToPrevQuestion">Предыдущий вопрос</button>
                  <div class="attempt-card__nav-counter">{{ currentQuestionNumber }} / {{ questions.length }}</div>
                  <button type="button" class="button secondary attempt-card__nav-btn" :disabled="currentQuestionIndex >= questions.length - 1" @click="goToNextQuestion">Следующий вопрос</button>
                </div>

                <button
                  v-if="!locked && isLastQuestion"
                  type="submit"
                  class="button primary wide attempt-card__mobile-submit"
                  :disabled="isSubmitting"
                >{{ isSubmitting ? 'Отправляем...' : 'Завершить тестирование' }}</button>
              </section>
            </form>
          </div>

          <div v-else class="final-testing-state">В этой попытке пока нет вопросов.</div>
        </template>
      </div>
    </div>

    <BaseModal :show="!!explanationQuestion" title="Пояснение к ответу">
      <template #description-slot>
        <div class="explanation-modal">
          <div class="explanation-modal__title">{{ explanationQuestion?.name }}</div>
          <div class="explanation-modal__text">{{ explanationQuestion?.right_comment }}</div>
        </div>
      </template>
      <button class="button primary" @click="closeExplanation">Понятно</button>
    </BaseModal>
  </div>
</template>

<script>
import { computed, inject, onMounted, onUnmounted, ref, watch } from "@vue/runtime-core";
import { useRoute } from "vue-router";
import BaseModal from "@/components/common/BaseModal";
import { getFinalTestingAttempt, getFinalTestingAttemptResult, sendFinalTestingAttempt } from "@/data/requests";

const RECENT_TESTING_KEY = "cmp_recent_testing";
const FINAL_PROGRESS_PREFIX = "cmp_final_testing_progress:";

const normalizeQuestion = (question) => ({
  ...question,
  name: question?.name ?? question?.title ?? "Вопрос",
  description: question?.description ?? question?.text ?? "",
  answers: question?.answers ?? [],
  compare_answers: question?.compare_answers ?? question?.compareAnswers ?? [],
  right_comment: question?.right_comment ?? "",
});

const buildForm = (questions) => questions.reduce((acc, question) => {
  acc[question.id] = { question_id: question.id, type: question.type };
  if (question.type === "compare") acc[question.id].answer_compare = {};
  else if (question.type === "multi") acc[question.id].answer_ids = [];
  else if (question.type === "one") acc[question.id].answer_id = null;
  else if (question.type === "text") acc[question.id].answer_text = "";
  return acc;
}, {});

const normalizeCompareAnswer = (id, value) => ({ id: Number.isNaN(Number(id)) ? id : Number(id), answer_compare: value });

function readStorage(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeStorage(key, value) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

function removeStorage(key) {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(key);
}

export default {
  components: { BaseModal },
  setup() {
    const logout = inject("logout");
    const token = inject("token");
    const route = useRoute();
    const stableAttemptId = String(route.params.attemptId ?? "");
    const attempt = ref(null);
    const questions = ref([]);
    const form = ref({});
    const formErrors = ref({});
    const generalError = ref("");
    const result = ref(null);
    const isLoading = ref(true);
    const isSubmitting = ref(false);
    const locked = ref(false);
    const activeQuestionId = ref(null);
    const explanationQuestion = ref(null);
    const questionLocks = ref({});
    const remainingSeconds = ref(0);
    let timer = null;

    const currentPath = `/final-testing/${stableAttemptId}`;
    const progressStorageKey = `${FINAL_PROGRESS_PREFIX}${stableAttemptId}`;
    const attemptBlocks = computed(() => attempt.value?.blocks ?? result.value?.blocks ?? []);
    const answeredCount = computed(() => questions.value.reduce((sum, question) => sum + (isAnswered(question) ? 1 : 0), 0));
    const progressPercent = computed(() => !questions.value.length ? 0 : Math.round((answeredCount.value / questions.value.length) * 100));
    const currentQuestionIndex = computed(() => questions.value.findIndex((question) => question.id === activeQuestionId.value));
    const currentQuestion = computed(() => {
      if (!questions.value.length) return null;
      const index = currentQuestionIndex.value >= 0 ? currentQuestionIndex.value : 0;
      return questions.value[index] ?? null;
    });
    const currentQuestionNumber = computed(() => currentQuestionIndex.value >= 0 ? currentQuestionIndex.value + 1 : 1);
    const isLastQuestion = computed(() => questions.value.length > 0 && currentQuestionIndex.value === questions.value.length - 1);
    const remainingLabel = computed(() => {
      const total = Math.max(remainingSeconds.value, 0);
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    });

    function saveRecentTesting() {
      writeStorage(RECENT_TESTING_KEY, {
        type: "final",
        path: currentPath,
        title: attempt.value?.id ? `Итоговое тестирование №${attempt.value.id}` : "Итоговое тестирование",
        savedAt: Date.now(),
      });
    }

    function clearRecentTesting() {
      const recent = readStorage(RECENT_TESTING_KEY);
      if (recent?.path === currentPath) removeStorage(RECENT_TESTING_KEY);
    }

    function clearProgress() {
      removeStorage(progressStorageKey.value);
      clearRecentTesting();
    }

    function restoreProgress() {
      const snapshot = readStorage(progressStorageKey);
      if (!snapshot || snapshot?.path !== currentPath) return;

      remainingSeconds.value = Math.max(Number(snapshot.remainingSeconds) || remainingSeconds.value, 0);
      questionLocks.value = snapshot.questionLocks && typeof snapshot.questionLocks === "object" ? snapshot.questionLocks : {};

      const statuses = snapshot.statuses && typeof snapshot.statuses === "object" ? snapshot.statuses : {};
      if (Object.keys(statuses).length) {
        questions.value = questions.value.map((question) => ({ ...question, status: statuses[question.id] ?? question.status ?? null }));
      }

      questions.value.forEach((question) => {
        const saved = snapshot.form?.[question.id];
        if (!saved || !form.value[question.id]) return;

        if (question.type === "one") {
          form.value[question.id].answer_id = saved.answer_id ?? null;
        } else if (question.type === "multi") {
          form.value[question.id].answer_ids = Array.isArray(saved.answer_ids) ? saved.answer_ids : [];
        } else if (question.type === "text") {
          form.value[question.id].answer_text = saved.answer_text ?? "";
        } else if (question.type === "compare") {
          form.value[question.id].answer_compare = saved.answer_compare && typeof saved.answer_compare === "object" ? saved.answer_compare : {};
        }
      });

      if (questions.value.some((question) => question.id === snapshot.activeQuestionId)) {
        activeQuestionId.value = snapshot.activeQuestionId;
      }
    }

    function persistProgress() {
      if (isLoading.value || locked.value || !attempt.value?.id || !questions.value.length) return;
      const statuses = questions.value.reduce((acc, question) => {
        if (question.status) acc[question.id] = question.status;
        return acc;
      }, {});

      writeStorage(progressStorageKey, {
        path: currentPath,
        title: attempt.value?.id ? `Итоговое тестирование №${attempt.value.id}` : "Итоговое тестирование",
        remainingSeconds: remainingSeconds.value,
        activeQuestionId: activeQuestionId.value,
        questionLocks: questionLocks.value,
        statuses,
        form: form.value,
        savedAt: Date.now(),
      });
      saveRecentTesting();
    }

    function stopTimer() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function startTimer() {
      stopTimer();
      if (locked.value) return;
      timer = setInterval(() => {
        if (remainingSeconds.value <= 1) {
          remainingSeconds.value = 0;
          stopTimer();
          submitAttempt(true);
          return;
        }

        remainingSeconds.value -= 1;
      }, 1000);
    }

    function hydrateAttempt(payload) {
      attempt.value = payload;
      questions.value = (payload?.questions ?? []).map(normalizeQuestion);
      form.value = buildForm(questions.value);
      formErrors.value = {};
      generalError.value = "";
      explanationQuestion.value = null;
      questionLocks.value = {};
      activeQuestionId.value = questions.value[0]?.id ?? null;
      remainingSeconds.value = payload?.remaining_seconds ?? payload?.duration_limit_seconds ?? 0;
      locked.value = !!payload?.is_completed || payload?.status === "passed" || payload?.status === "failed";
      if (!locked.value) restoreProgress();
      if (locked.value) clearProgress(); else saveRecentTesting();
    }

    function applyResult(resultData) {
      result.value = resultData;
      locked.value = true;
      questionLocks.value = questions.value.reduce((acc, question) => { acc[question.id] = true; return acc; }, {});
      questions.value = questions.value.map((question) => {
        const found = (resultData?.result ?? []).find((item) => item.question?.id === question.id);
        if (!found) return { ...question, status: null };
        return { ...normalizeQuestion(found.question), status: found.status, user_answer: found.user_answer };
      });

      questions.value.forEach((question) => {
        const userAnswer = question.user_answer;
        if (!userAnswer || !form.value[question.id]) return;
        if (question.type === "one") form.value[question.id].answer_id = userAnswer.answer_id;
        else if (question.type === "multi") form.value[question.id].answer_ids = userAnswer.answer_ids ?? [];
        else if (question.type === "text") form.value[question.id].answer_text = userAnswer.answer_text ?? "";
        else if (question.type === "compare") {
          const mapped = {};
          (userAnswer.answer_compare ?? []).forEach((item) => { mapped[item.id] = item.answer_compare; });
          form.value[question.id].answer_compare = mapped;
        }
      });

      if (!questions.value.find((question) => question.id === activeQuestionId.value)) {
        activeQuestionId.value = questions.value[0]?.id ?? null;
      }

      stopTimer();
      clearProgress();
    }

    async function loadResult() {
      const resultData = await getFinalTestingAttemptResult(token.value, stableAttemptId);
      applyResult(resultData);
    }

    async function loadAttempt() {
      isLoading.value = true;
      generalError.value = "";

      try {
        const response = await getFinalTestingAttempt(token.value, stableAttemptId);
        hydrateAttempt(response?.attempt ?? response?.data ?? response);
        if (locked.value) await loadResult();
        else startTimer();
      } catch (error) {
        generalError.value = error?.message || "Не удалось загрузить итоговое тестирование";
      } finally {
        isLoading.value = false;
      }
    }

    function setQuestionStatus(questionId, status) {
      questions.value = questions.value.map((question) => question.id === questionId ? { ...question, status } : question);
    }

    function isQuestionLocked(question) {
      return locked.value || !!questionLocks.value[question.id];
    }

    function isQuestionChecked(question) {
      return locked.value || !!questionLocks.value[question.id];
    }

    function lockQuestion(questionId) {
      questionLocks.value = { ...questionLocks.value, [questionId]: true };
    }

    function ensureQuestionForm(question) {
      if (!question || form.value[question.id]) return;
      const next = buildForm([question]);
      form.value = { ...form.value, [question.id]: next[question.id] };
    }

    function getRightAnswersCount(question) {
      return (question.answers ?? []).filter((answer) => !!answer.right).length;
    }

    function validateQuestionLocally(question) {
      const current = form.value[question.id];
      if (!current) return null;

      if (question.type === "one") {
        return question.answers?.some((answer) => String(answer.id) === String(current.answer_id) && !!answer.right) ? "right" : "wrong";
      }

      if (question.type === "multi") {
        const selectedIds = (current.answer_ids ?? []).map(String).sort();
        const rightIds = (question.answers ?? []).filter((answer) => !!answer.right).map((answer) => String(answer.id)).sort();
        return JSON.stringify(selectedIds) === JSON.stringify(rightIds) ? "right" : "wrong";
      }

      if (question.type === "compare") {
        const allRight = (question.answers ?? []).every((answer) => {
          const chosen = current.answer_compare?.[answer.id];
          return chosen && chosen === answer.answer_compare;
        });
        return allRight ? "right" : "wrong";
      }

      return null;
    }

    function submitSingleChoice(question, answerId) {
      if (isQuestionLocked(question)) return;
      ensureQuestionForm(question);
      form.value[question.id].answer_id = answerId;
      setQuestionStatus(question.id, validateQuestionLocally(question));
      lockQuestion(question.id);
      goToNextQuestion();
    }

    function isMultiChoiceDisabled(question, answerId) {
      if (isQuestionLocked(question)) return true;
      const selectedIds = form.value[question.id]?.answer_ids ?? [];
      if (selectedIds.map(String).includes(String(answerId))) return false;
      return selectedIds.length >= getRightAnswersCount(question);
    }

    function toggleMultiAnswer(question, answerId, checked) {
      if (isQuestionLocked(question) || !checked) return;
      ensureQuestionForm(question);
      const selectedIds = form.value[question.id].answer_ids ?? [];
      if (!selectedIds.map(String).includes(String(answerId))) {
        form.value[question.id].answer_ids = [...selectedIds, answerId];
      }

      if (form.value[question.id].answer_ids.length >= getRightAnswersCount(question)) {
        setQuestionStatus(question.id, validateQuestionLocally(question));
        lockQuestion(question.id);
        goToNextQuestion();
      }
    }

    function updateCompareAnswer(question, answerId, value) {
      if (isQuestionLocked(question)) return;
      ensureQuestionForm(question);
      form.value[question.id].answer_compare = {
        ...form.value[question.id].answer_compare,
        [answerId]: value,
      };

      const allFilled = (question.answers ?? []).every((answer) => !!form.value[question.id].answer_compare?.[answer.id]);
      if (allFilled) {
        setQuestionStatus(question.id, validateQuestionLocally(question));
        lockQuestion(question.id);
        goToNextQuestion();
      }
    }

    function finalizeTextAnswer(question) {
      if (isQuestionLocked(question)) return;
      ensureQuestionForm(question);
      if (!form.value[question.id]?.answer_text?.trim()) return;
      lockQuestion(question.id);
      goToNextQuestion();
    }

    function isAnswered(question) {
      const current = form.value[question.id];
      if (!current) return false;
      if (question.type === "one") return current.answer_id !== null && current.answer_id !== undefined;
      if (question.type === "multi") return Array.isArray(current.answer_ids) && current.answer_ids.length > 0;
      if (question.type === "compare") return Object.values(current.answer_compare ?? {}).filter(Boolean).length > 0;
      if (question.type === "text") return !!current.answer_text?.trim();
      return false;
    }

    function isSelected(question, answerId) {
      const current = form.value[question.id];
      if (!current) return false;
      if (question.type === "one") return String(current.answer_id) === String(answerId);
      if (question.type === "multi") return current.answer_ids.map(String).includes(String(answerId));
      return false;
    }

    function isCorrectAnswer(question, answerId) {
      return isQuestionChecked(question) && question.answers?.some((answer) => answer.id === answerId && !!answer.right);
    }

    function isWrongSelectedAnswer(question, answerId) {
      return isQuestionChecked(question) && isSelected(question, answerId) && !isCorrectAnswer(question, answerId);
    }

    function canShowExplanation(question) {
      return isQuestionChecked(question) && !!question.right_comment && question.status !== "right";
    }

    function getCompareValue(question, answerId) {
      return form.value[question.id]?.answer_compare?.[answerId] ?? "";
    }

    function getTextAnswer(question) {
      return form.value[question.id]?.answer_text ?? "";
    }

    function updateTextAnswer(question, value) {
      ensureQuestionForm(question);
      form.value[question.id].answer_text = value;
    }

    function openExplanation(question) {
      explanationQuestion.value = question;
    }

    function closeExplanation() {
      explanationQuestion.value = null;
    }

    function setActiveQuestion(questionId) {
      activeQuestionId.value = questionId;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function goToPrevQuestion() {
      if (currentQuestionIndex.value <= 0) return;
      activeQuestionId.value = questions.value[currentQuestionIndex.value - 1]?.id ?? activeQuestionId.value;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function goToNextQuestion() {
      if (currentQuestionIndex.value >= questions.value.length - 1) return;
      activeQuestionId.value = questions.value[currentQuestionIndex.value + 1]?.id ?? activeQuestionId.value;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function serializeAnswers() {
      return questions.value.map((question) => {
        const answer = form.value[question.id];
        if (question.type === "one") return { question_id: question.id, answer_id: answer.answer_id };
        if (question.type === "multi") return { question_id: question.id, answer_ids: answer.answer_ids };
        if (question.type === "text") return { question_id: question.id, answer_text: answer.answer_text };
        return {
          question_id: question.id,
          answer_compare: Object.entries(answer.answer_compare ?? {})
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([id, value]) => normalizeCompareAnswer(id, value)),
        };
      });
    }

    function questionError(index) {
      const keys = [
        `answers.${index}.question_id`,
        `answers.${index}.answer_id`,
        `answers.${index}.answer_ids`,
        `answers.${index}.answer_text`,
        `answers.${index}.answer_compare`,
      ];
      for (const key of keys) {
        if (formErrors.value[key]) return Array.isArray(formErrors.value[key]) ? formErrors.value[key][0] : formErrors.value[key];
      }
      return "";
    }

    async function submitAttempt(isAutoSubmit = false) {
      if (locked.value || isSubmitting.value) return;
      isSubmitting.value = true;
      formErrors.value = {};
      generalError.value = "";
      const totalSeconds = attempt.value?.duration_limit_seconds ?? 0;
      const spentSeconds = Math.max(totalSeconds - remainingSeconds.value, 1);
      const response = await sendFinalTestingAttempt(token.value, stableAttemptId, {
        seconds: spentSeconds,
        answers: serializeAnswers(),
      });
      isSubmitting.value = false;

      if (response.code === 422) {
        formErrors.value = response.data?.errors ?? {};
        generalError.value = response.data?.message ?? (isAutoSubmit ? "Время вышло, но сервер не принял попытку. Проверьте незаполненные вопросы." : "Проверьте заполнение итогового тестирования");
        return;
      }

      await loadResult();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function dotClass(question) {
      return {
        "is-active": activeQuestionId.value === question.id,
        "is-success": question.status === "right",
        "is-failed": ["wrong", "part"].includes(question.status),
        "is-answered": isAnswered(question),
      };
    }

    function cardClass(question) {
      return {
        "attempt-card--with-image": question.image_url,
        "attempt-card--right": question.status === "right",
        "attempt-card--wrong": question.status === "wrong",
        "attempt-card--part": question.status === "part",
      };
    }

    function choiceClass(question, answerId) {
      return {
        "attempt-choice--selected": isSelected(question, answerId),
        "attempt-choice--radio": question.type === "one",
        "attempt-choice--correct": isCorrectAnswer(question, answerId),
        "attempt-choice--wrong": isWrongSelectedAnswer(question, answerId),
      };
    }

    function compareClass(question, answer) {
      const chosen = form.value[question.id]?.answer_compare?.[answer.id];
      return {
        "attempt-compare-row--correct": locked.value && chosen && answer.answer_compare && chosen === answer.answer_compare,
        "attempt-compare-row--wrong": locked.value && chosen && answer.answer_compare && chosen !== answer.answer_compare,
      };
    }

    function textClass(question) {
      return {
        "attempt-text-answer--correct": locked.value && question.status === "right",
        "attempt-text-answer--wrong": locked.value && ["wrong", "part"].includes(question.status),
      };
    }

    watch(form, persistProgress, { deep: true });
    watch(questionLocks, persistProgress, { deep: true });
    watch(remainingSeconds, persistProgress);
    watch(activeQuestionId, persistProgress);

    onMounted(loadAttempt);
    onUnmounted(() => {
      persistProgress();
      stopTimer();
    });

    return {
      logout, attempt, attemptBlocks, questions, form, result, isLoading, isSubmitting, locked, generalError,
      answeredCount, progressPercent, remainingLabel, explanationQuestion,
      currentQuestion, currentQuestionIndex, currentQuestionNumber, isLastQuestion,
      isQuestionLocked, submitSingleChoice, isMultiChoiceDisabled, toggleMultiAnswer, updateCompareAnswer, finalizeTextAnswer,
      canShowExplanation, openExplanation, closeExplanation, submitAttempt,
      setActiveQuestion, goToPrevQuestion, goToNextQuestion,
      getCompareValue, getTextAnswer, updateTextAnswer,
      dotClass, cardClass, choiceClass, compareClass, textClass, questionError,
      isSelected, isCorrectAnswer, isWrongSelectedAnswer,
    };
  },
};
</script>

<style scoped lang="scss">
.content-main_desc { max-width: 1240px; width: 100%; }
.final-testing-state { margin-top: 24px; font-size: 16px; }
.attempt-hero {
  margin-top: 20px;
  padding: 32px;
  border-radius: 30px;
  color: #fff;
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(280px, .95fr);
  gap: 24px;
  background: radial-gradient(circle at top left, rgba(255,255,255,.16), transparent 26%), linear-gradient(135deg, #331f6b 0%, #045c93 45%, #10a17c 100%);
}
.attempt-hero__eyebrow { font-size: 12px; text-transform: uppercase; letter-spacing: .12em; opacity: .82; margin-bottom: 10px; }
.attempt-hero__title { margin: 0 0 12px; font-size: 40px; line-height: 1.05; }
.attempt-hero__subtitle { margin: 0; max-width: 740px; color: rgba(255,255,255,.88); line-height: 1.7; }
.attempt-hero__chips { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px; }
.attempt-hero__chip { padding: 10px 14px; border-radius: 999px; background: rgba(255,255,255,.14); }
.attempt-hero__stats, .attempt-result__stats { display: grid; gap: 12px; }
.attempt-hero__stats div, .attempt-result__stats div { padding: 16px 18px; border-radius: 18px; background: rgba(255,255,255,.14); }
.attempt-hero__stats span, .attempt-result__stats span { display: block; font-size: 12px; opacity: .82; margin-bottom: 8px; }
.attempt-hero__stats strong, .attempt-result__stats strong { font-size: 30px; line-height: 1; }
.attempt-result { margin-top: 18px; padding: 24px; border-radius: 26px; display: grid; grid-template-columns: minmax(0,1.3fr) minmax(320px,1fr); gap: 24px; color: #fff; }
.attempt-result--success { background: linear-gradient(135deg, #0d8d73 0%, #17bb91 100%); }
.attempt-result--warning { background: linear-gradient(135deg, #bf5c17 0%, #e58a29 100%); }
.attempt-result__eyebrow { opacity: .8; text-transform: uppercase; letter-spacing: .08em; font-size: 12px; }
.attempt-result__score { font-size: 56px; line-height: 1; margin: 8px 0 10px; }
.attempt-result__summary { line-height: 1.6; }
.attempt-result__blocked { margin-top: 12px; color: rgba(255,255,255,.84); }
.attempt-layout { margin-top: 24px; display: grid; grid-template-columns: 290px minmax(0,1fr); gap: 24px; align-items: start; }
.attempt-sidebar { position: sticky; top: 20px; }
.attempt-sidebar__back { display: inline-block; margin-bottom: 14px; color: #0a6ca8; text-decoration: none; }
.attempt-sidebar__panel { background: linear-gradient(180deg, #f4f8ff 0%, #eef8fb 100%); border: 1px solid #d9e6f2; border-radius: 24px; padding: 20px; }
.attempt-sidebar__label { font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: #678298; }
.attempt-sidebar__time { font-size: 34px; margin: 8px 0 6px; color: #16344d; }
.attempt-sidebar__meta { color: #607d91; margin-bottom: 14px; }
.attempt-sidebar__progress { height: 12px; border-radius: 999px; background: rgba(10, 122, 191, .12); overflow: hidden; }
.attempt-sidebar__progress span { display: block; height: 100%; background: linear-gradient(90deg, #ff9946 0%, #0a7abf 48%, #12b48c 100%); }
.attempt-sidebar__rule { margin-top: 16px; color: #48657a; line-height: 1.5; }
.attempt-sidebar__grid { margin-top: 18px; display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 10px; }
.attempt-sidebar__dot { border: none; border-radius: 14px; height: 44px; font-weight: 700; color: #5c7488; background: #fff; cursor: pointer; }
.attempt-sidebar__dot.is-active { outline: 2px solid #0a7abf; color: #0a7abf; }
.attempt-sidebar__dot.is-answered { background: #eef8ff; color: #0a7abf; box-shadow: inset 0 0 0 2px rgba(10, 122, 191, .28); }
.attempt-sidebar__dot.is-success { background: #dff6f2; color: #0b8f7f; box-shadow: inset 0 0 0 2px rgba(11, 143, 127, .24); }
.attempt-sidebar__dot.is-failed { background: #fff1ec; color: #d95f22; box-shadow: inset 0 0 0 2px rgba(217, 95, 34, .22); }
.attempt-sidebar__hint { margin-top: 16px; color: #557286; font-size: 13px; line-height: 1.55; }
.attempt-sidebar__submit { margin-top: 16px; text-align: center; width: 100%; box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; }
.attempt-form { display: grid; gap: 20px; }
.attempt-form__error { padding: 16px 18px; border-radius: 18px; background: #fff2f4; border: 1px solid #ffd1d9; color: #b42343; }
.attempt-card { background: radial-gradient(circle at top right, rgba(10,122,191,.08), transparent 25%), #fff; border: 1px solid #e2ecf4; border-radius: 28px; padding: 28px; box-shadow: 0 18px 40px rgba(15,76,129,.08); }
.attempt-card--right { border-color: #8dd6be; }
.attempt-card--wrong, .attempt-card--part { border-color: #ffc9b3; }
.attempt-card__head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 20px; }
.attempt-card__index { font-size: 13px; text-transform: uppercase; letter-spacing: .08em; color: #6a8294; margin-bottom: 8px; }
.attempt-card__title { margin: 0; font-size: 30px; line-height: 1.1; color: #16344d; }
.attempt-card__meta { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.attempt-card__meta-chip { padding: 8px 12px; border-radius: 999px; background: #eef7ff; color: #0a6ca8; font-size: 12px; font-weight: 700; }
.attempt-card__meta-chip--subtle { background: #f4f7fb; color: #5e788c; }
.attempt-card__help { width: 34px; height: 34px; border-radius: 50%; border: none; background: #ffe1cf; color: #c1551d; font-weight: 700; font-size: 18px; cursor: pointer; }
.attempt-card__body { display: grid; grid-template-columns: minmax(0,1fr) 300px; gap: 24px; }
.attempt-card__description { margin: 0 0 18px; color: #425b6f; line-height: 1.7; font-size: 16px; }
.attempt-card__answers { display: grid; gap: 14px; }
.attempt-card__nav { margin-top: 24px; display: flex; align-items: center; justify-content: space-between; gap: 14px; }
.attempt-card__nav-btn { min-width: 190px; justify-content: center; }
.attempt-card__nav-counter { color: #5f7a8d; font-weight: 700; }
.attempt-card__mobile-submit { display: none; margin-top: 14px; justify-content: center; }
.attempt-choice { position: relative; display: flex; align-items: center; gap: 14px; padding: 18px; border-radius: 20px; border: 1px solid #d9e6f2; background: #fbfdff; cursor: pointer; transition: border-color .2s ease, box-shadow .2s ease, background-color .2s ease, transform .2s ease; }
.attempt-choice input { position: absolute; opacity: 0; pointer-events: none; }
.attempt-choice__control { position: relative; width: 22px; min-width: 22px; height: 22px; border-radius: 8px; border: 2px solid #d5e4f0; background: #f1f7fb; box-sizing: border-box; }
.attempt-choice__control::after { content: ""; position: absolute; top: 50%; left: 50%; width: 10px; height: 10px; transform: translate(-50%, -50%) scale(0); border-radius: 3px; background: #fff; transition: transform .18s ease; }
.attempt-choice--radio .attempt-choice__control { border-radius: 999px; }
.attempt-choice--radio .attempt-choice__control::after { border-radius: 999px; width: 8px; height: 8px; }
.attempt-choice__text { flex: 1 1 auto; color: #233b4d; }
.attempt-choice__mark { font-size: 12px; font-weight: 700; text-transform: uppercase; }
.attempt-choice__mark--correct { color: #0b8f7f; }
.attempt-choice__mark--wrong { color: #c1551d; }
.attempt-choice--selected { border-color: #0a7abf; background: linear-gradient(180deg, #f4fbff 0%, #eaf5ff 100%); box-shadow: 0 0 0 2px rgba(10, 122, 191, .14), 0 14px 28px rgba(10, 122, 191, .10); transform: translateY(-1px); }
.attempt-choice--selected .attempt-choice__control { background: #0a7abf; border-color: #0a7abf; }
.attempt-choice--selected .attempt-choice__control::after { transform: translate(-50%, -50%) scale(1); }
.attempt-choice--correct { border-color: #8dd6be; background: #effaf6; }
.attempt-choice--correct .attempt-choice__control { background: #16a07d; border-color: #16a07d; }
.attempt-choice--correct .attempt-choice__control::after { transform: translate(-50%, -50%) scale(1); }
.attempt-choice--wrong { border-color: #ffb899; background: #fff4ef; }
.attempt-choice--wrong .attempt-choice__control { background: #d95f22; border-color: #d95f22; }
.attempt-choice--wrong .attempt-choice__control::after { transform: translate(-50%, -50%) scale(1); }
.attempt-compare-grid { display: grid; gap: 12px; }
.attempt-compare-row { display: grid; grid-template-columns: minmax(180px,1fr) minmax(0,1.4fr); gap: 14px; align-items: center; padding: 14px; border-radius: 18px; border: 1px solid #e0e9f1; background: #fbfdff; }
.attempt-compare-row--correct { border-color: #8dd6be; background: #effaf6; }
.attempt-compare-row--wrong { border-color: #ffb899; background: #fff4ef; }
.attempt-compare-row__field { position: relative; }
.attempt-compare-row__select { width: 100%; padding: 16px 44px 16px 16px; border-radius: 16px; border: 1px solid #d3e2ee; background: #f8fbfe; font: inherit; appearance: none; }
.attempt-compare-row__chevron { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); color: #7c93a6; pointer-events: none; }
.attempt-compare-row__right-answer { grid-column: 1 / -1; color: #567085; font-size: 13px; }
.attempt-text-answer { border-radius: 18px; padding: 14px; border: 1px solid #d3e2ee; background: #fbfdff; }
.attempt-text-answer--correct { border-color: #8dd6be; background: #effaf6; }
.attempt-text-answer--wrong { border-color: #ffb899; background: #fff4ef; }
.attempt-text-answer__label { display: block; margin-bottom: 10px; color: #284559; font-weight: 600; }
.attempt-text-answer__field { width: 100%; min-height: 140px; padding: 18px; border-radius: 18px; border: 1px solid #d3e2ee; background: #fff; font: inherit; resize: vertical; }
.attempt-question-error { color: #b42343; font-size: 14px; }
.attempt-card__media { align-self: start; background: linear-gradient(180deg, #f7fbff 0%, #eef5fb 100%); border-radius: 24px; overflow: hidden; min-height: 220px; }
.attempt-card__media img { display: block; width: 100%; height: 100%; object-fit: cover; }
.explanation-modal__title { font-weight: 700; font-size: 18px; margin-bottom: 12px; }
.explanation-modal__text { white-space: pre-wrap; line-height: 1.6; }
@media all and (max-width: 1100px) { .attempt-layout, .attempt-result { grid-template-columns: 1fr; } .attempt-sidebar { position: static; } }
@media all and (max-width: 900px) { .attempt-hero { grid-template-columns: 1fr; padding: 24px; } .attempt-hero__title { font-size: 30px; } .attempt-card__body, .attempt-compare-row { grid-template-columns: 1fr; } }
@media all and (max-width: 640px) { .attempt-sidebar__submit { display: none; } .attempt-card__mobile-submit { display: inline-flex; } .attempt-card { padding: 20px; border-radius: 22px; } .attempt-card__title { font-size: 24px; } .attempt-card__head { flex-direction: column; } .attempt-card__nav { flex-direction: column; align-items: stretch; } .attempt-card__nav-btn { min-width: 0; width: 100%; } }
</style>




























